import { useCallback, useMemo } from 'react'
import { giveGenericHandleChangedData } from 'src/consumer/helpers/utilFunctions'

/**
 * Custom hook for prescription body handlers
 * Handles boolean fields and EMR section style overrides
 * Following SRP, DRY, and KISS principles
 */
export function usePrescriptionBodyHandlers(setPrescriptionTemplate) {
  // Handler for boolean fields in body (hideEmptyDrugAdviceColumns, showPatientPhoneOnPrescription, etc.)
  const handleBodyBooleanChange = useCallback(
    (e) => {
      setPrescriptionTemplate((prev) => ({
        ...prev,
        body: {
          ...(prev?.body || {}),
          [e.target.id]: e.target.checked,
        },
      }))
    },
    [setPrescriptionTemplate],
  )

  // Handler for scalar value fields in body (e.g. drugAdviceTablePrintType radio/select)
  const handleBodyValueChange = useCallback(
    (fieldId, value) => {
      setPrescriptionTemplate((prev) => ({
        ...prev,
        body: {
          ...(prev?.body || {}),
          [fieldId]: value,
        },
      }))
    },
    [setPrescriptionTemplate],
  )

  // Handler for style overrides of a specific EMR section
  const handleBodyEmrSectionStyleChange = useCallback(
    (emrSectionId, eventOrUpdater) => {
      setPrescriptionTemplate((prev) => {
        const currentStyleOverrides = prev?.body?.styleOverridesForPrintedEmrSections || {}
        const currentSectionStyle = currentStyleOverrides[emrSectionId] || {}

        return {
          ...prev,
          body: {
            ...(prev?.body || {}),
            styleOverridesForPrintedEmrSections: {
              ...currentStyleOverrides,
              [emrSectionId]: giveGenericHandleChangedData(eventOrUpdater, currentSectionStyle),
            },
          },
        }
      })
    },
    [],
  )

  // Creates a curried setOverrideData function for a specific EMR section
  // This follows the pattern: (setObjOrPrevData) => { setStyleOverrideData(emrSectionId, setObjOrPrevData) }
  const createSetOverrideData = useCallback(
    (emrSectionId) => {
      return (eventOrUpdater) => {
        handleBodyEmrSectionStyleChange(emrSectionId, eventOrUpdater)
      }
    },
    [handleBodyEmrSectionStyleChange],
  )

  // Memoize the return object to prevent unnecessary re-renders
  return useMemo(
    () => ({
      handleBodyBooleanChange,
      handleBodyValueChange,
      handleBodyEmrSectionStyleChange,
      createSetOverrideData,
    }),
    [
      handleBodyBooleanChange,
      handleBodyValueChange,
      handleBodyEmrSectionStyleChange,
      createSetOverrideData,
    ],
  )
}
